import type { AppState } from '../state/store';
import type { PassStatus } from '../api/types';
import { getPhantomProvider } from '../auth/wallet';
import { btn, esc, pill, row, shortAddr } from './components';
import { formatLocalDateTime } from './format';
import { iconRow } from './icons';
import { rewardLoadoutPanel } from './loadoutPanel';

function connectPanel(state: AppState): string {
  const hasPhantom = getPhantomProvider() !== null;
  const phantomNote = hasPhantom
    ? ''
    : `<p class="muted">Phantom not detected. Install the Phantom extension, or use a dev test wallet below.</p>`;
  const devPanel = state.devMode
    ? `<div class="stack">
         <p class="muted">Dev test wallet — a throwaway keypair signed with real crypto (DEV builds only). Lets you test the full loop without Phantom.</p>
         <div class="btn-row">
           ${btn('Use dev test wallet', 'connect-dev', {}, { variant: 'primary' })}
           ${btn('New dev wallet (fresh account)', 'new-dev-wallet', {}, { variant: 'ghost' })}
         </div>
       </div>`
    : '';
  return `
    <section class="card">
      <h2>Connect</h2>
      <p class="muted">Prove wallet ownership with an off-chain signature — no transaction, no gas, no $OBOL spent.</p>
      <div class="btn-row">
        ${btn('Connect Phantom', 'connect-phantom', {}, { variant: 'primary', disabled: !hasPhantom })}
      </div>
      ${phantomNote}
      ${devPanel}
    </section>`;
}

function accountPanel(state: AppState): string {
  const a = state.account;
  return `
    <section class="card">
      <h2>Account</h2>
      ${row('Wallet', `<code>${esc(shortAddr(state.walletAddress))}</code> ${pill(state.walletKind ?? '—', state.walletKind === 'dev' ? 'warn' : 'muted')}`)}
      ${row('Account ID', `<code>${esc(shortAddr(a?.account.id ?? null))}</code>`)}
      ${row('Status', pill(a?.status ?? 'unknown', a?.status === 'active' ? 'ok' : 'warn'))}
      <div class="btn-row">${btn('Refresh', 'refresh-account', {}, { variant: 'ghost' })}</div>
    </section>`;
}

function eligibilityKind(stateValue: string | undefined): 'ok' | 'warn' | 'down' | 'muted' {
  switch (stateValue) {
    case 'active': case 'eligible': return 'ok';
    case 'frozen': case 'under': return 'down';
    case 'unverified': return 'warn';
    default: return 'muted';
  }
}

function eligibilityPanel(state: AppState): string {
  const e = state.eligibility;
  const last = e?.state ?? e?.last_check_result ?? null;
  const balance = e?.balance ?? e?.cached_balance;
  return `
    <section class="card">
      <h2>Eligibility (hold-to-play gate)</h2>
      ${row('State', pill(e?.eligibility_state ?? 'unactivated', eligibilityKind(e?.eligibility_state)))}
      ${row('Last check', last ? pill(last, eligibilityKind(last)) : '<span class="muted">never</span>')}
      ${row('Gate balance', balance !== undefined ? `<code>${esc(balance)}</code>` : '<span class="muted">unknown</span>')}
      ${e?.last_checked_at ? row('Checked at', `<span class="muted" title="${esc(e.last_checked_at)}">${esc(formatLocalDateTime(e.last_checked_at))}</span>`) : ''}
      <div class="btn-row">
        ${btn('Re-check (live RPC)', 'refresh-eligibility', {}, { variant: 'primary' })}
        ${btn('Status only', 'refresh-eligibility-status', {}, { variant: 'ghost' })}
      </div>
      <p class="hint">An RPC error returns <em>unverified</em> and never freezes you. Your $OBOL balance is re-verified live whenever it matters — starting a Reward run, earning a Pass, or claiming rewards.</p>
    </section>`;
}

function passPanel(state: AppState): string {
  const p = state.pass;
  if (!p) {
    return `<section class="card"><h2>Ferry Passes</h2><div class="btn-row">${btn('Load Pass status', 'refresh-pass', {}, { variant: 'ghost' })}</div></section>`;
  }
  const next = formatFerryPassCountdown(p);
  const title = p.next_regen_at ? ` title="${esc(p.next_regen_at)}"` : '';
  return `
    <section class="card">
      <h2>Ferry Passes</h2>
      ${iconRow('Balance', 'ferryPass', `<strong>${esc(p.balance)}</strong> / ${esc(p.cap)}`)}
      ${row('Eligibility', pill(p.eligibility_state, eligibilityKind(p.eligibility_state)))}
      ${row('Next +1', `<span class="muted" data-ferry-pass-countdown data-next-regen-at="${esc(p.next_regen_at ?? '')}" data-pass-balance="${esc(p.balance)}" data-pass-cap="${esc(p.cap)}" data-pass-frozen="${esc(String(p.frozen))}" data-pass-eligibility="${esc(p.eligibility_state)}"${title}>${esc(next)}</span>`)}
      <div class="btn-row">${btn('Refresh', 'refresh-pass', {}, { variant: 'ghost' })}</div>
      ${p.eligibility_state !== 'active' ? '<p class="hint">Pass regeneration requires active $OBOL eligibility. Re-check eligibility to resume earning Passes.</p>' : ''}
      ${p.eligibility_state === 'active' && p.balance === 0 ? '<p class="hint">No Pass to spend — a Reward run needs balance ≥ 1.</p>' : ''}
    </section>`;
}

function countdownParts(remainingMs: number): { hours: number; minutes: number; seconds: number } {
  const totalSeconds = Math.ceil(Math.max(0, remainingMs) / 1000);
  return {
    hours: Math.floor(totalSeconds / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
  };
}

export function formatFerryPassCountdown(pass: PassStatus, nowMs = Date.now()): string {
  if (pass.balance >= pass.cap) return 'At cap';
  if (pass.frozen || pass.eligibility_state !== 'active') return 'Paused / frozen';
  if (!pass.next_regen_at) return 'Ready to refresh';
  const nextMs = Date.parse(pass.next_regen_at);
  if (!Number.isFinite(nextMs) || nextMs <= nowMs) return 'Ready to refresh';
  const { hours, minutes, seconds } = countdownParts(nextMs - nowMs);
  if (hours > 0) return `Next +1 in ${hours}h ${minutes}m`;
  return `Next +1 in ${minutes}m ${String(seconds).padStart(2, '0')}s`;
}

export function updateFerryPassCountdowns(root: Pick<ParentNode, 'querySelectorAll'>, nowMs = Date.now()): void {
  const labels = root.querySelectorAll<HTMLElement>('[data-ferry-pass-countdown]');
  for (const label of labels) {
    const pass: PassStatus = {
      balance: Number(label.dataset.passBalance ?? 0),
      cap: Number(label.dataset.passCap ?? 0),
      frozen: label.dataset.passFrozen === 'true',
      eligibility_state: (label.dataset.passEligibility as PassStatus['eligibility_state']) ?? 'unactivated',
      last_regen_at: null,
      next_regen_at: label.dataset.nextRegenAt || null,
    };
    label.textContent = formatFerryPassCountdown(pass, nowMs);
  }
}

function runStartPanel(state: AppState): string {
  const eligible = state.eligibility?.eligibility_state === 'active';
  const hasPass = (state.pass?.balance ?? 0) > 0;
  const rewardBlocked = !eligible || !hasPass;
  const blockReason = !eligible ? 'wallet not active/eligible' : !hasPass ? 'no Ferry Pass' : '';
  const resume = state.runId
    ? `<p class="hint">Active run <code>${esc(state.runId)}</code> — ${btn('resume', 'goto-run', {}, { variant: 'ghost' })}</p>`
    : '';
  return `
    <section class="card card--wide">
      <h2>Descend</h2>
      <div class="btn-row">
        ${btn('Start Echo run', 'start-echo', {}, { variant: 'primary' })}
        ${btn('Start Reward run', 'start-reward', {}, { variant: rewardBlocked ? 'ghost' : 'primary', title: rewardBlocked ? blockReason : 'spend a Pass and descend' })}
      </div>
      <p class="muted">Echo = no gate, no Pass, no rewards (free practice). Reward = spend a Pass, seal loot, bank Gold.</p>
      ${rewardLoadoutPanel(state)}
      ${rewardBlocked ? `<p class="hint">Reward run likely blocked: ${esc(blockReason)}. You can still try — the server is authoritative.</p>` : ''}
      ${resume}
    </section>`;
}

export function renderDashboard(state: AppState): string {
  if (!state.session) {
    return `<div class="grid">${connectPanel(state)}</div>`;
  }
  return `
    <div class="grid">
      ${accountPanel(state)}
      ${eligibilityPanel(state)}
      ${passPanel(state)}
      ${runStartPanel(state)}
    </div>`;
}
